import { CATEGORY_PATH_IDENTIFIER_DELIMITER } from "@faire/retailer-shared/categories/pathIdentifier";
import { buildFilterKey } from "@faire/retailer-shared/filters/filterKeys";
import { AspectRatioPicture } from "@faire/retailer-visitor-shared/components/Image/AspectRatioPicture";
import {
  getAllBrandsLink,
  getBrandValueLink,
} from "@faire/retailer-visitor-shared/consts/allBrandsLinks";
import { EAEAEA_PIXEL } from "@faire/retailer-visitor-shared/consts/EAEAEA_PIXEL";
import { getNoMinimumFilterLink } from "@faire/retailer-visitor-shared/consts/noMinimumBrandsValues";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Carousel } from "@faire/web/slate-labs/Carousel/Carousel";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Link } from "@faire/web/ui/LinkAnchor";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React from "react";

export interface ITile {
  c1PathIdentifier?: string;
  label: string;
  mobileLabel?: string;
  link: string;
  imageUrl: string;
}

export const useTiles = (): ITile[] => {
  const { strictLocalize } = useStrictLocalization();
  return [
    {
      c1PathIdentifier: "home_living",
      label: strictLocalize({
        defaultMessage: "Shop home decor",
        description: { text: "Button to shop the 'home decor' category" },
      }),
      link: "/category/Home Decor",
      imageUrl:
        "https://cdn.faire.com/fastly/4e1184f7c9c7a3da4c682abafdd856d7f824b5788b47617914a1d1fd358019f9.jpeg",
    },
    {
      label: strictLocalize({
        defaultMessage: "Brands not on Amazon",
        description: { text: "Button to shop brands that are not on Amazon" },
      }),
      mobileLabel: strictLocalize({
        defaultMessage: "Not on Amazon",
        description: { text: "Button to shop brands that are not on Amazon" },
      }),
      link: getBrandValueLink("not-sold-on-amazon"),
      imageUrl:
        "https://cdn.faire.com/fastly/673e3e2396ebe861807a27891f1354e5993cbe9e9d473cd8e0e31a5b9677c8cb.jpeg",
    },
    {
      c1PathIdentifier: "women",
      label: strictLocalize({
        defaultMessage: "Shop women",
        description: { text: "Button to shop the 'women' category" },
      }),
      link: "/category/Women",
      imageUrl:
        "https://cdn.faire.com/fastly/15cf08198a86dba2e5f9a0ad13729203d5cdfb14d19f3440bda5b045c0c5ef46.jpeg",
    },
    {
      label: strictLocalize({
        defaultMessage: "Brands with short lead times",
        description: {
          text: "Button to shop brands with fast turnaround time between ordering and shipping",
        },
      }),
      mobileLabel: strictLocalize({
        defaultMessage: "Short lead times",
        description: {
          text: "Button to shop brands with fast turnaround time between ordering and shipping",
        },
      }),
      link: getAllBrandsLink({
        filters: [buildFilterKey("lead_time", "three_or_less_days")],
      }),
      imageUrl:
        "https://cdn.faire.com/fastly/2780e295939308b2d9d00203a07a3265aae5c48cb89462faa4c1c7bd08ea8e0c.jpeg",
    },
    {
      label: strictLocalize({
        defaultMessage: "Brands with no minimums",
        description: {
          text: "Button to shop brands with no minimum spending requirement",
        },
      }),
      mobileLabel: strictLocalize({
        defaultMessage: "Shop no minimums",
        description: {
          text: "Button to shop brands with no minimum spending requirement",
        },
      }),
      link: getNoMinimumFilterLink(),
      imageUrl:
        "https://cdn.faire.com/fastly/8e93056c61be9579cb61b6d790a4bd99cae4d46cee723ec77d4f15543b8d33bd.jpeg",
    },
    {
      c1PathIdentifier: "beauty_wellness",
      label: strictLocalize({
        defaultMessage: "Shop beauty & wellness",
        description: {
          text: "Button to shop the 'beaty and wellness' category",
        },
      }),
      mobileLabel: strictLocalize({
        defaultMessage: "Shop wellness",
        description: {
          text: "Button to shop the 'beaty and wellness' category",
        },
      }),
      link: "/category/Beauty & Wellness",
      imageUrl:
        "https://cdn.faire.com/fastly/990565ba2b650a2f1f508bdbe70491ba3638d9ccf5e4fc9a6609a6f8dbf69f10.jpeg",
    },
    {
      c1PathIdentifier: "jewelry",
      label: strictLocalize({
        defaultMessage: "Shop jewelry",
        description: { text: "Button to shop the 'jewelry' category" },
      }),
      link: "/category/Jewelry",
      imageUrl:
        "https://cdn.faire.com/fastly/d12fd27df0ffc01e76a9a329b67bc9333f0244abb4b5d6c860a603841c76d5fb.jpeg",
    },
    {
      c1PathIdentifier: "food_drink",
      label: strictLocalize({
        defaultMessage: "Shop food & drink",
        description: { text: "Button to shop the 'food and drink' category" },
      }),
      link: "/category/Food & Drink",
      imageUrl:
        "https://cdn.faire.com/fastly/c0510d4a93f4ff78f090e96c8043b435af8a3a7368c2a6213ecd4980e2166e5b.jpeg",
    },
    {
      c1PathIdentifier: "paper_novelty",
      label: strictLocalize({
        defaultMessage: "Shop paper & novelty",
        description: {
          text: "Button to shop the 'paper and novelty' category",
        },
      }),
      link: "/category/Paper & Novelty",
      imageUrl:
        "https://cdn.faire.com/fastly/2713dfe3e844999b6e152438381abec12a2974c84b9ded0f08819ff869bf4a72.jpeg",
    },
    {
      label: strictLocalize({
        defaultMessage: "Eco-Friendly brands",
        description: { text: "Button to shop the 'eco-friendly' category" },
      }),
      link: getBrandValueLink("eco-friendly"),
      imageUrl:
        "https://cdn.faire.com/fastly/749ffe09238499ddd51da5399555e1a3e14c757e1b06e3829306ae36dbb1df2d.jpeg",
    },
  ];
};

interface IProps {
  highlightCategoryPath?: string;
  removeCategoryPath?: string;
  clickTrackPath: string;
  dataTestId?: string;
  onClick?: () => void;
}

export const KeepExploringCarousel: React.FC<IProps> = (props) => {
  const { isMobile } = useViewport();

  const handleClick = (clickType: string) => () => {
    trackEvent(`${props.clickTrackPath}/${clickType}`);
    props.onClick?.();
  };

  const isSSR = useIsSSR();

  const renderTile = (item: ITile, index: number) => {
    return (
      <Column align="center" key={index}>
        <Link
          to={item.link}
          style={{ width: "100%" }}
          onClick={handleClick("tile-click")}
        >
          <AspectRatioPicture $ratio={4 / 5} $width="100%" $hideBorder>
            <WhenVisibilityIsImminent
              forceRender={isPrerender()}
              customizePlaceholder={
                <img
                  src={optimizeImageUrl({ url: EAEAEA_PIXEL }, isSSR, {
                    width: 360,
                    height: 450,
                    resizeMethod: "fill",
                  })}
                  alt={item.label}
                />
              }
            >
              <img
                src={optimizeImageUrl(
                  { url: item.imageUrl ?? EAEAEA_PIXEL },
                  isSSR,
                  { width: 360, height: 450, resizeMethod: "fill" }
                )}
                alt={item.label}
              />
            </WhenVisibilityIsImminent>
          </AspectRatioPicture>
        </Link>
        <Spacer height="2x" />
        <Button
          variant="tertiary"
          linkTo={item.link}
          onClick={handleClick("tile-click")}
          data-test-id={`keep-exploring-carousel-label-${item.label}`}
        >
          <ResponsiveContentV2
            mobile={item.mobileLabel ?? item.label}
            tabletAndAbove={item.label}
          />
        </Button>
      </Column>
    );
  };

  const cta = (
    <Button
      variant="tertiary"
      linkTo={getAllBrandsLink()}
      onClick={handleClick("cta-click")}
      style={{ justifyContent: "end", flex: 1 }}
    >
      <StrictLocalMsg
        defaultMessage="All Brands"
        description={{ text: "Button to see all the brands" }}
      />
    </Button>
  );

  const tiles = useTiles();
  let tilesToDisplay = [...tiles];

  if (props.highlightCategoryPath) {
    const c1PathIdentifier = props.highlightCategoryPath.split(
      CATEGORY_PATH_IDENTIFIER_DELIMITER
    )[0];
    const highlight = tiles.find(
      (tile) => tile.c1PathIdentifier === c1PathIdentifier
    );
    tilesToDisplay = tiles.filter(
      (tile) => tile.c1PathIdentifier !== c1PathIdentifier
    );
    if (highlight) {
      tilesToDisplay.unshift(highlight);
    }
  }

  if (props.removeCategoryPath) {
    const c1PathIdentifier = props.removeCategoryPath.split(
      CATEGORY_PATH_IDENTIFIER_DELIMITER
    )[0];
    tilesToDisplay = tiles.filter(
      (tile) => tile.c1PathIdentifier !== c1PathIdentifier
    );
  }

  return (
    <Column data-test-id={props.dataTestId}>
      <Row style={{ padding: isMobile ? getSpacing(0, "2x") : undefined }}>
        {/* title is left aligned on mobile, and is cenetered on tabletAndAbove */}
        {!isMobile ? <div style={{ flex: 1 }} /> : null}
        <Typography
          variant="pageHeaderSerifRegular"
          align={isMobile ? "start" : "center"}
          style={{ flex: 1 }}
        >
          <StrictLocalMsg
            defaultMessage="Keep exploring"
            description={{
              text: "Title for a carousel/set of additional brands to look at",
            }}
          />
        </Typography>
        {cta}
      </Row>
      <Spacer height={{ mobileAndAbove: "2x", tabletAndAbove: "4x" }} />
      <Carousel
        itemsPerSlide={isMobile ? 2 : 3}
        items={tilesToDisplay}
        renderItem={renderTile}
        itemGap={isMobile ? 8 : 16}
        showNavigationArrows={{
          mobileAndAbove: false,
          desktopAndAbove: true,
        }}
        arrowOffsetX={0}
      />
    </Column>
  );
};
